import {
  getCountries,
  login,
  register,
  updateRequiredData,
  sendPasswordResetEmail,
  resetPassword,
  changePassword,
  refreshPlayerAuthentication,
} from '@/utils/api';
import i18n from '@/utils/i18n';
import { setDefaults } from '@/utils/apiAxios';
import { LoginTypes } from '@/utils/enums';
import Vue from 'vue';
import Vuex from 'vuex';
import { getField, updateField } from 'vuex-map-fields';
import router from '@/router';
import { startCheckInactivity } from '@/plugins/playerInactivityChecker';
import { log } from '@/utils/logging';
import snq from 'snq';

Vue.use(Vuex);

const getDefaultState = () => {
  return {
    loginForm: {
      email: '',
      password: '',
      isClerk: false,
    },
    loginStatus: {
      loading: false,
      success: false,
      error: false,
    },
    registerForm: {
      firstName: '',
      lastName: '',
      country: 'US',
      email: '',
      password: '',
      passwordRepeat: '',
      mobilePhone: '',
      birthMonth: '',
      birthDay: '',
      birthYear: '',
      howDidYouHear: '',
      otherField: '',
      promoCode: '',
      agreeTos: false,
      newsCheckbox: true,
      zipCode: '',
      state: '',
      countries: [],
    },
    registerStatus: {
      loading: false,
      success: false,
      error: false,
    },
    forgotPasswordForm: {
      email: '',
    },
    forgotPasswordFormStatus: {
      loading: false,
      success: false,
      error: false,
    },
    resetPasswordForm: {
      password: '',
      passwordRepeat: '',
    },
    resetPasswordFormStatus: {
      loading: false,
      success: false,
      error: false,
    },
    smsVerificationRequested: localStorage.getItem('sms-verification-requested'), //     STEP_1 = default           STEP_2 = requested        STEP_3 process the verification
    editAccountInformationForm: {
      oldPassword: '',
      newPassword: '',
      newPasswordRepeat: '',
    },
    editAccountInformationFormStatus: {
      loading: false,
      success: false,
      error: false,
    },
    reqiuredStatus: {
      loading: false,
      success: false,
      error: false,
    },
  };
};

const state = getDefaultState();

const getters = {
  getField,
};

const mutations = {
  updateField,
  updateLoginStatus(state, row) {
    state.loginStatus = {
      ...state.loginStatus,
      ...row,
    };
  },
  updateRegisterStatus(state, row) {
    state.registerStatus = {
      ...state.registerStatus,
      ...row,
    };
  },
  updateReqiuredStatus(state, row) {
    state.reqiuredStatus = {
      ...state.reqiuredStatus,
      ...row,
    };
  },
  updateForgotPasswordFormStatus(state, row) {
    state.forgotPasswordFormStatus = {
      ...state.forgotPasswordFormStatus,
      ...row,
    };
  },
  updateResetPasswordFormStatus(state, row) {
    state.resetPasswordFormStatus = {
      ...state.resetPasswordFormStatus,
      ...row,
    };
  },
  setCountries(state, arr) {
    state.registerForm.countries = arr;
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setIsClerk(state, isClerk) {
    state.loginForm.isClerk = isClerk;
  },
  setSmsVerificationRequested(state, requested) {
    state.smsVerificationRequested = requested;
    localStorage.setItem('sms-verification-requested', requested);
  },
  updateEditAccountInformationFormStatus(state, row) {
    state.editAccountInformationFormStatus = {
      ...state.editAccountInformationFormStatus,
      ...row,
    };
  },
  resetEditAccountInformationForm(state) {
    state.editAccountInformationForm = {
      oldPassword: '',
      newPassword: '',
      newPasswordRepeat: '',
    };
  },
};

const actions = {
  tryLogin({ commit, dispatch, rootState, rootGetters }, { email, password, isClerk, timeForInactivity, redirect }) {
    log('[AUTH] login attempt with username: ' + email);
    commit('updateLoginStatus', {
      loading: true,
      success: false,
      error: false,
    });
    login(email, password, isClerk, rootState.settings.macAddress).then(r => {
      window.scrollTo(0, 0);
      let mustRedirectToPasswordChange = false;
      commit('updateLoginStatus', { loading: false });
      if (r.data.IsSuccess) {
        if (isClerk) {
          const force_password_change = !!snq(() => r.data.Data.data.force_password_change);
          mustRedirectToPasswordChange = force_password_change;
        }

        if (r.data.ExceptionMessage) {
          commit('updateLoginStatus', { success: false });
          commit('updateLoginStatus', {
            error: i18n.t('Exceptions.' + r.data.ExceptionMessage[0]),
          });
          setTimeout(() => {
            commit('updateLoginStatus', { error: false });
          }, 3000);
          return;
        }
        if (r.data.Data.Player && r.data.Data.Player.PlayerMessages.length > 0) {
          r.data.Data.Player.PlayerMessages = r.data.Data.Player.PlayerMessages.map(item => ({
            ...item,
            status: true,
          }));
        }
        commit('updateLoginStatus', { success: i18n.t('LoginView.Success') });
        if (timeForInactivity != -1) {
          startCheckInactivity(timeForInactivity);
        }
        commit('updateLoginStatus', { error: false });
        commit('betSlip/setShowLoginNeedModal', false, { root: true });
        if (isClerk) {
          const innerResponse = r.data.Data.data;
          if (innerResponse.sessionId) {
            localStorage.setItem('clerkSessionID', innerResponse.sessionId);
          } else {
            Vue.prototype.$modal.openErrorModal({
              content: 'fe_sessionId_is_empty',
            });
          }

          if (innerResponse.id) {
            commit('player/setToken', innerResponse.token, { root: true });
            commit('player/setLoginType', LoginTypes.Clerk, { root: true });
            commit(
              'player/setClerk',
              {
                account_name: innerResponse.first_name,
                user_id: innerResponse.id,
                mustRedirectToPasswordChange,
              },
              { root: true },
            );
            setDefaults();
          } else {
            commit('updateLoginStatus', { success: false });
            commit('updateLoginStatus', { error: i18n.t('LoginView.Error') });
            setTimeout(() => {
              commit('updateLoginStatus', { error: false });
            }, 3000);
            return;
          }
        } else {
          commit('player/setToken', r.data.Data.Token, { root: true });
          commit('player/setLoginType', LoginTypes.Player, { root: true });

          commit('player/setPlayer', r.data.Data.Player, { root: true });
          setDefaults();
          dispatch('player/getPlayerData', null, { root: true });
          dispatch('betSlip/getBetTabs', null, { root: true });
          dispatch('betSlip/getNewBetOptions', { checkLineAvailability: true }, { root: true });

          /* TIMEZONE ID */
          const timeZoneId = r.data.Data.Player.PlayerInfo.DGS.schedule_timezone_id;
          // const timeZone = rootGetters["settings/getTimeZoneById"](timeZoneId.toString());
          commit('settings/setDefaultTimeZone', timeZoneId, { root: true });
        }
        setTimeout(() => {
          commit('depositTicketStore/resetState', null, { root: true });
          commit('clerkPayoutDepositTicketStore/resetState', null, { root: true });
          if (mustRedirectToPasswordChange) {
            router.push('/clerk/change-password');
          } else {
            router.replace(redirect);
          }
          commit('updateLoginStatus', { success: false });
        }, 2000);
      } else {
        commit('updateLoginStatus', { success: false });
        commit('updateLoginStatus', { error: i18n.t('LoginView.Error') });
        setTimeout(() => {
          commit('updateLoginStatus', { error: false });
        }, 2000);
      }
    });
  },
  tryRegister(
    { commit, rootState, dispatch },
    {
      email,
      password,
      passwordRepeat,
      firstName,
      lastName,
      promoCode,
      hearAbout,
      hearAboutDetail,
      refererSignup,
      signupDeviceType,
      signupDeviceAgent,
      remoteIp,
      language,
      requestDomain,
      agreeTos,
      doNotEmail,
      country,
      BirthDay,
      phone_number,
      AffToken,
    },
  ) {
    log('[AUTH] register attempt');
    if (password === '') {
      commit('updateRegisterStatus', {
        error: i18n.t('RegisterView.EmptyPassword'),
      });
      return;
    }

    if (password !== passwordRepeat) {
      commit('updateRegisterStatus', {
        error: i18n.t('RegisterView.PasswordRepeatWrong'),
      });
      return;
    }

    commit('updateRegisterStatus', {
      loading: true,
      success: false,
      error: false,
    });
    register(
      email,
      password,
      passwordRepeat,
      firstName,
      lastName,
      promoCode,
      hearAbout,
      hearAboutDetail,
      refererSignup,
      signupDeviceType,
      signupDeviceAgent,
      remoteIp,
      language,
      requestDomain,
      agreeTos,
      doNotEmail,
      country,
      BirthDay,
      phone_number,
      AffToken,
    ).then(r => {
      if (r.data.IsSuccess && !r.data.ExceptionMessage) {
        commit('updateRegisterStatus', {
          success: i18n.t('RegisterView.RegisterSuccess'),
        });
        commit('updateRegisterStatus', { error: false });

        commit('player/setPlayer', r.data.Data.Player, { root: true });
        commit('player/setToken', r.data.Data.Token, { root: true });
        commit('player/setLoginType', LoginTypes.Player, { root: true });
        setDefaults();
        dispatch('player/getPlayerData', null, { root: true });
        dispatch('betSlip/getBetTabs', null, { root: true });
        dispatch('betSlip/getNewBetOptions', { checkLineAvailability: true }, { root: true });
        router.push('/');
        setTimeout(() => {
          if (rootState.settings.clientType === 'ELECTRON') router.push('/');
          else router.push('/deposit');
          commit('updateRegisterStatus', { success: false });
        }, 2000);
      } else {
        commit('updateRegisterStatus', { success: false });
        commit('updateRegisterStatus', {
          error: i18n.t('Exceptions.' + r.data.ExceptionMessage[0]),
        });
      }
      commit('updateRegisterStatus', { loading: false });
    });
  },
  tryGetCountries({ commit, rootState }) {
    getCountries(rootState.settings.brandSettings.book_id, rootState.settings.selectedLocale.short_code).then(r => {
      if (r.data.IsSuccess) {
        commit('setCountries', r.data.Data);
      }
    });
  },
  trySendPasswordResetEmail({ commit }) {
    commit('updateForgotPasswordFormStatus', {
      loading: true,
      success: false,
      error: false,
    });
    sendPasswordResetEmail(state.forgotPasswordForm.email).then(r => {
      if (r.data.IsSuccess && r.data.Data) {
        commit('updateForgotPasswordFormStatus', {
          success: i18n.t('ForgotPasswordView.SendEmailSuccess'),
        });
      } else {
        commit('updateForgotPasswordFormStatus', {
          error: i18n.t('ForgotPasswordView.SendEmailError'),
        });
      }
      commit('updateForgotPasswordFormStatus', { loading: false });
    });
  },
  tryResetPassword({ commit }, { password_token }) {
    log('[AUTH] reset password attempt');
    if (state.resetPasswordForm.password !== state.resetPasswordForm.passwordRepeat) {
      commit('updateResetPasswordFormStatus', {
        error: i18n.t('RegisterView.PasswordRepeatWrong'),
      });
      return;
    }

    commit('updateResetPasswordFormStatus', {
      loading: true,
      success: false,
      error: false,
    });
    resetPassword(password_token, state.resetPasswordForm.password).then(r => {
      if (r.data.IsSuccess && !r.data.Data.error) {
        commit('updateResetPasswordFormStatus', {
          success: i18n.t('ForgotPasswordView.ResetSuccess'),
        });
      } else {
        if (r.data.Data.error === 'invalid_token') {
          commit('updateResetPasswordFormStatus', {
            error: i18n.t('ForgotPasswordView.InvalidToken'),
          });
        } else {
          commit('updateResetPasswordFormStatus', {
            error: i18n.t('ForgotPasswordView.SendEmailError'),
          });
        }
      }
      commit('updateResetPasswordFormStatus', { loading: false });
    });
  },
  tryChangePassword({ commit }, payload) {
    log('[AUTH] change password attempt');
    commit('updateEditAccountInformationFormStatus', {
      loading: true,
      success: false,
      error: false,
    });

    const oldPassword = payload && payload.oldPassword;
    const newPassword = payload && payload.newPassword;
    const playerAccount = payload && payload.playerAccount;

    changePassword({
      account: playerAccount,
      old_password: oldPassword,
      new_password: newPassword,
    }).then(r => {
      if (r.data.IsSuccess && !r.data.Data.error) {
        commit('updateEditAccountInformationFormStatus', {
          success: 'ChangePassword_Password changed successfully',
        });
        commit('resetEditAccountInformationForm');
      } else {
        commit('updateEditAccountInformationFormStatus', {
          error: 'ChangePassword_There was an error while request',
        });
      }
      commit('updateEditAccountInformationFormStatus', { loading: false });
    });
  },
  tryRefreshPlayerAuthentication({ commit, rootState }) {
    refreshPlayerAuthentication().then(r => {
      commit('updateLoginStatus', { loading: false });
      if (r.data.IsSuccess) {
        commit('player/setToken', r.data.Data.Token, { root: true });

        commit('player/setPlayer', r.data.Data.Player, { root: true });
        setDefaults();

        location.reload();
        if (rootState.authStore.smsVerificationRequested === 'STEP_2') {
          commit('setSmsVerificationRequested', 'STEP_3');
        }
      } else {
        commit('updateLoginStatus', { success: false });
        commit('updateLoginStatus', { error: i18n.t('LoginView.Error') });
        setTimeout(() => {
          commit('updateLoginStatus', { error: false });
        }, 2000);
      }
    });
  },
  tryUpdateRequiredData({ dispatch, commit }, payload) {
    updateRequiredData(payload).then(r => {
      commit('updateReqiuredStatus', { loading: false });
      if (r.data.IsSuccess && r.data.Data) {
        if (payload.verifyRequested) commit('setSmsVerificationRequested', 'STEP_2');
        commit('updateReqiuredStatus', { success: true });
        dispatch('tryRefreshPlayerAuthentication');
      } else {
        commit('updateReqiuredStatus', { success: false });
        commit('updateReqiuredStatus', {
          error: 'There was an error while request',
        });
      }
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
